import {
  type Paginated,
  type SubscriptionProduct,
  type SubscriptionProductCreateRequest,
  type SubscriptionProductDetail,
  type SubscriptionProductUpdateRequest,
  type SubscriptionProductsListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own INPUT shape (U3) — never a hand-written copy of the filters. */
type ProductsParams = z.input<typeof SubscriptionProductsListQuerySchema>;

/**
 * What the platform sells, and for how long — the `Subscriptions` module's api.
 *
 * ## The prices are NOT here, and that is the api's decision rather than a gap
 *
 * `SubscriptionProductCreateSchema` and its update partial carry no
 * `directPrices`: a direct price is a **scoped setting**
 * (`billing.directPrices`, `scopesAllowed: ['global', 'gender']`), so it is
 * written through `PUT /admin/settings/:key` on the shared settings api and read
 * back here only as `SubscriptionProductDetail.directPrices` — a resolved
 * `{ global, male, female }` triple the api composes from three `getSetting`
 * reads. That is why `useDirectPrices` exists beside this file rather than an
 * endpoint below, and why a price write invalidates a product: the two live in
 * different caches and the detail restates one of them.
 *
 * ## A write returns the row and the screen still re-reads
 *
 * `POST`/`PATCH` answer with the product, and writing that straight into the
 * cache would save a round trip. Deliberately not done, for the reason every
 * other api in this portal records: rendering from a mutation response gives the
 * screen two sources for one row, and they disagree the moment a colleague saves
 * a second later.
 *
 * ## One api for the module, and `Payments` will have its own
 *
 * The catalogue's reactive edges stay inside it. The direct-order queue (P23b2)
 * reads products but never writes one, so it bridges through the shared product
 * **lookup** (W6) and the `billing.product.changed` event rather than sharing
 * this cache — the division `user-lookup` already draws for members.
 */
export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SubscriptionProducts', 'SubscriptionProduct'],
  endpoints: (builder) => ({
    /**
     * The catalogue.
     *
     * `SubscriptionProductsService.list` goes through the localized paginator,
     * so `sort`/`order` are honoured — but only against **base-table** columns,
     * which is why the name column carries no `sortKey` (it lives in
     * `subscription_product_translations`).
     */
    listProducts: builder.query<Paginated<SubscriptionProduct>, ProductsParams>({
      query: (params) => ({ url: '/admin/subscription-products', method: 'GET', params }),
      providesTags: ['SubscriptionProducts'],
    }),

    /** One product with EVERY locale — what the ar/en tabs prefill from (W7). */
    product: builder.query<SubscriptionProductDetail, string>({
      query: (id) => ({ url: `/admin/subscription-products/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'SubscriptionProduct' as const, id }],
    }),

    createProduct: builder.mutation<SubscriptionProductDetail, SubscriptionProductCreateRequest>({
      query: (body) => ({ url: '/admin/subscription-products', method: 'POST', body }),
      invalidatesTags: ['SubscriptionProducts'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceProductChange(queryFulfilled.then(({ data }) => data.id));
      },
    }),

    updateProduct: builder.mutation<
      SubscriptionProductDetail,
      { id: string; body: SubscriptionProductUpdateRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/subscription-products/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'SubscriptionProduct' as const, id },
        'SubscriptionProducts' as const,
      ],
      async onQueryStarted({ id }, { queryFulfilled }) {
        await announceProductChange(queryFulfilled.then(() => id));
      },
    }),

    /**
     * A soft delete, and the api keeps every entitlement already sold under the
     * key — which is why the confirmation says "withdraw" rather than "delete".
     */
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/subscription-products/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'SubscriptionProduct' as const, id },
        'SubscriptionProducts' as const,
      ],
      async onQueryStarted(id, { queryFulfilled }) {
        await announceProductChange(queryFulfilled.then(() => id));
      },
    }),
  }),
});

async function announceProductChange(landed: Promise<string | null>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'billing.product.changed', productId: await landed });
  } catch {
    // Refused — nothing to announce. Swallowed deliberately: `onQueryStarted`
    // has no caller, so a rejection escaping it becomes an unhandled rejection
    // that exits the runner non-zero beside a wholly green suite (P22b1). The
    // failure is already the caller's, through `unwrap()`.
  }
}

export const {
  useListProductsQuery,
  useProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} = subscriptionsApi;
